/**
 * LeadsKanban.tsx — Vista Kanban del pipeline de leads.
 *
 * QUÉ HACE: pinta los leads en columnas por `status` y permite moverlos entre etapas.
 * El cambio de columna dispara onStatusChange(leadId, nuevoStatus) → el ORQUESTADOR
 * (LeadsPage) persiste. Este componente NO llama al API: recibe datos y emite eventos.
 *
 * RESPONSIVE — dos interacciones distintas a propósito:
 *  · Desktop (lg+): drag & drop real con @dnd-kit.
 *  · Móvil: selector de etapa + botón "mover" por tarjeta. Arrastrar dentro de un
 *    scroll horizontal pelea con el gesto de scroll (problema conocido); Trello
 *    resuelve igual. Menos elegante en teoría, mucho más usable en la mano.
 *
 * Optimista: la tarjeta se mueve al instante; si el API falla, el orquestador revierte.
 */

import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  useDroppable, useDraggable, closestCorners,
  type DragStartEvent, type DragEndEvent
} from '@dnd-kit/core'
import { Phone, Mail, MessageSquare, Eye, ChevronLeft, ChevronRight, Plus, CalendarClock, AlertTriangle } from 'lucide-react'
import { LEAD_STATUSES, isRotting } from './constants'
import { getStatusAccent, getTemperatureIcon, getScoreBarClass, useCrmFormatters } from './helpers'
import type { Lead } from './types'

interface LeadsKanbanProps {
  leads: Lead[]
  onStatusChange: (leadId: string, newStatus: string) => void
  onOpenDetail: (lead: Lead) => void
  onAddActivity: (lead: Lead) => void
}

// ── Tarjeta ──────────────────────────────────────
interface CardProps { lead: Lead; onOpenDetail: (l: Lead) => void; onAddActivity: (l: Lead) => void; dragging?: boolean }

/**
 * Badge de seguimiento — la señal más importante de la tarjeta.
 * Tres estados, en orden de urgencia:
 *   🔴 vencida     → la próxima actividad ya pasó
 *   🟡 sin agenda  → no hay actividad programada (el lead se va a enfriar)
 *   🔵 programada  → todo en orden, muestra cuándo
 * Patrón Pipedrive: la tarjeta debe gritar qué requiere atención sin abrirla.
 */
const ActivityBadge: React.FC<{ lead: Lead; onAdd: (l: Lead) => void }> = ({ lead, onAdd }) => {
  const { t } = useTranslation('crm')
  const { formatDateShort, formatTime } = useCrmFormatters()

  if (!lead.next_activity_at) {
    return (
      <button onClick={() => onAdd(lead)}
        className="w-full flex items-center justify-center gap-1 py-1 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[10px] hover:bg-amber-500/25 transition-colors">
        <Plus className="w-3 h-3" />{t('badge.noActivity')}
      </button>
    )
  }

  const when = new Date(lead.next_activity_at)
  const now = new Date()
  const isOverdue = when < now
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1)
  const dayAfter = new Date(tomorrow); dayAfter.setDate(dayAfter.getDate() + 1)

  let label: string
  if (isOverdue) label = t('badge.overdue')
  else if (when < tomorrow) label = `${t('badge.today')} ${formatTime(lead.next_activity_at)}`
  else if (when < dayAfter) label = `${t('badge.tomorrow')} ${formatTime(lead.next_activity_at)}`
  else label = `${formatDateShort(lead.next_activity_at)} ${formatTime(lead.next_activity_at)}`

  return (
    <div className={`w-full flex items-center gap-1 px-2 py-1 rounded-md text-[10px] border ${
      isOverdue ? 'bg-red-500/15 border-red-500/30 text-red-300' : 'bg-blue-500/10 border-blue-500/20 text-blue-300'}`}>
      <CalendarClock className="w-3 h-3 shrink-0" />
      <span className="truncate">{label}</span>
    </div>
  )
}

const LeadCardContent: React.FC<CardProps> = ({ lead, onOpenDetail, onAddActivity, dragging }) => {
  const { t } = useTranslation('crm')
  const { formatDateShort } = useCrmFormatters()
  const rotting = isRotting(lead.status, lead.days_idle)

  return (
    <div className={`bg-slate-800/80 border rounded-xl p-3 space-y-2 transition-shadow ${
      dragging ? 'shadow-2xl ring-2 ring-blue-400/50 border-white/10' :
      rotting ? 'border-red-500/40 hover:border-red-500/60' : 'border-white/10 hover:border-white/20'}`}>
      {rotting && (
        <div className="flex items-center gap-1 text-[10px] text-red-400">
          <AlertTriangle className="w-3 h-3" />{t('badge.idle', { count: lead.days_idle })}
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-white text-sm truncate">{lead.name || t('lead.noName')}</p>
          <p className="text-xs text-gray-400 truncate">{lead.phone || '—'}</p>
        </div>
        <span className="text-lg shrink-0" title={t(`temperature.${lead.temperature}`)}>
          {getTemperatureIcon(lead.temperature)}
        </span>
      </div>

      {(lead.property_type || lead.purpose) && (
        <p className="text-xs text-gray-300 truncate">
          {lead.property_type || t('lead.notSpecified')}
          {lead.purpose ? ` · ${lead.purpose}` : ''}
        </p>
      )}

      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
          <div className={`h-full ${getScoreBarClass(lead.score)}`} style={{ width: `${lead.score}%` }} />
        </div>
        <span className="text-[10px] text-gray-400 shrink-0">{lead.score}</span>
      </div>

      <div onPointerDown={(e) => e.stopPropagation()}>
        <ActivityBadge lead={lead} onAdd={onAddActivity} />
      </div>

      <div className="flex items-center justify-between pt-1 border-t border-white/5">
        <span className="text-[10px] text-gray-500">{formatDateShort(lead.created_at)}</span>
        <div className="flex items-center gap-0.5" onPointerDown={(e) => e.stopPropagation()}>
          <button onClick={() => onAddActivity(lead)} className="p-1.5 hover:bg-cyan-500/20 rounded-lg" title={t('badge.addActivity')}>
            <Plus className="w-3.5 h-3.5 text-cyan-400" />
          </button>
          <button onClick={() => onOpenDetail(lead)} className="p-1.5 hover:bg-blue-500/20 rounded-lg" title={t('actions.viewDetail')}>
            <Eye className="w-3.5 h-3.5 text-blue-400" />
          </button>
          {lead.phone && (
            <>
              <a href={`https://wa.me/${lead.phone.replace(/\D/g, '')}?text=${encodeURIComponent(t('whatsappGreeting', { name: lead.name || '' }))}`}
                 target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-green-500/20 rounded-lg" title={t('actions.whatsapp')}>
                <MessageSquare className="w-3.5 h-3.5 text-green-400" />
              </a>
              <a href={`tel:${lead.phone}`} className="p-1.5 hover:bg-blue-500/20 rounded-lg" title={t('actions.call')}>
                <Phone className="w-3.5 h-3.5 text-blue-400" />
              </a>
            </>
          )}
          {lead.email && (
            <a href={`mailto:${lead.email}`} className="p-1.5 hover:bg-purple-500/20 rounded-lg" title={t('actions.email')}>
              <Mail className="w-3.5 h-3.5 text-purple-400" />
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

const DraggableCard: React.FC<CardProps> = ({ lead, onOpenDetail, onAddActivity }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: lead.id })
  return (
    <div ref={setNodeRef} {...listeners} {...attributes}
         className={`cursor-grab active:cursor-grabbing touch-none ${isDragging ? 'opacity-30' : ''}`}>
      <LeadCardContent lead={lead} onOpenDetail={onOpenDetail} onAddActivity={onAddActivity} />
    </div>
  )
}

// ── Columna (desktop) ────────────────────────────
const Column: React.FC<{ status: string; leads: Lead[]; onOpenDetail: (l: Lead) => void; onAddActivity: (l: Lead) => void }> = ({ status, leads, onOpenDetail, onAddActivity }) => {
  const { t } = useTranslation('crm')
  const { setNodeRef, isOver } = useDroppable({ id: status })

  return (
    <div className="flex flex-col w-72 shrink-0">
      <div className="flex items-center justify-between px-1 pb-2">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${getStatusAccent(status)}`} />
          <h3 className="text-sm font-semibold text-white">{t(`status.${status}`)}</h3>
        </div>
        <span className="text-xs text-gray-400 bg-white/5 px-2 py-0.5 rounded-full">{leads.length}</span>
      </div>
      <div ref={setNodeRef}
           className={`flex-1 space-y-2 p-2 rounded-xl border transition-colors min-h-[120px] ${isOver ? 'bg-blue-500/10 border-blue-400/40' : 'bg-white/[0.02] border-white/5'}`}>
        {leads.map(lead => <DraggableCard key={lead.id} lead={lead} onOpenDetail={onOpenDetail} onAddActivity={onAddActivity} />)}
      </div>
    </div>
  )
}

// ── Principal ────────────────────────────────────
const LeadsKanban: React.FC<LeadsKanbanProps> = ({ leads, onStatusChange, onOpenDetail, onAddActivity }) => {
  const { t } = useTranslation('crm')
  const [activeId, setActiveId] = useState<string | null>(null)
  const [mobileIdx, setMobileIdx] = useState(0)

  // 8px de tolerancia: distingue un clic en los botones de la tarjeta de un arrastre real
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

  const byStatus = useMemo(() => {
    const map: Record<string, Lead[]> = {}
    LEAD_STATUSES.forEach(s => { map[s] = [] })
    leads.forEach(l => { (map[l.status] ??= []).push(l) })
    return map
  }, [leads])

  const activeLead = activeId ? leads.find(l => l.id === activeId) : null

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null)
    const leadId = String(e.active.id)
    const newStatus = e.over ? String(e.over.id) : null
    if (!newStatus || !LEAD_STATUSES.includes(newStatus)) return
    const lead = leads.find(l => l.id === leadId)
    if (lead && lead.status !== newStatus) onStatusChange(leadId, newStatus)
  }

  const mobileStatus = LEAD_STATUSES[mobileIdx]

  return (
    <>
      {/* DESKTOP — Kanban con drag & drop */}
      <div className="hidden lg:block">
        <DndContext sensors={sensors} collisionDetection={closestCorners}
                    onDragStart={(e: DragStartEvent) => setActiveId(String(e.active.id))}
                    onDragEnd={handleDragEnd} onDragCancel={() => setActiveId(null)}>
          <div className="flex gap-3 overflow-x-auto pb-4">
            {LEAD_STATUSES.map(status => (
              <Column key={status} status={status} leads={byStatus[status] || []} onOpenDetail={onOpenDetail} onAddActivity={onAddActivity} />
            ))}
          </div>
          <DragOverlay>
            {activeLead ? <div className="w-72"><LeadCardContent lead={activeLead} onOpenDetail={onOpenDetail} onAddActivity={onAddActivity} dragging /></div> : null}
          </DragOverlay>
        </DndContext>
      </div>

      {/* MÓVIL — una etapa a la vez, mover por botones */}
      <div className="lg:hidden space-y-3">
        <div className="flex items-center justify-between bg-white/5 rounded-xl p-2">
          <button onClick={() => setMobileIdx(i => Math.max(0, i - 1))} disabled={mobileIdx === 0}
                  className="p-2 rounded-lg hover:bg-white/10 disabled:opacity-30" aria-label="prev">
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${getStatusAccent(mobileStatus)}`} />
            <span className="text-sm font-semibold text-white">{t(`status.${mobileStatus}`)}</span>
            <span className="text-xs text-gray-400 bg-white/10 px-2 py-0.5 rounded-full">{byStatus[mobileStatus]?.length || 0}</span>
          </div>
          <button onClick={() => setMobileIdx(i => Math.min(LEAD_STATUSES.length - 1, i + 1))}
                  disabled={mobileIdx === LEAD_STATUSES.length - 1}
                  className="p-2 rounded-lg hover:bg-white/10 disabled:opacity-30" aria-label="next">
            <ChevronRight className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Puntos de progreso — orientación sin ocupar espacio */}
        <div className="flex justify-center gap-1.5">
          {LEAD_STATUSES.map((s, i) => (
            <button key={s} onClick={() => setMobileIdx(i)}
                    className={`h-1.5 rounded-full transition-all ${i === mobileIdx ? 'w-6 bg-blue-400' : 'w-1.5 bg-white/20'}`} />
          ))}
        </div>

        <div className="space-y-2">
          {(byStatus[mobileStatus] || []).length === 0 ? (
            <p className="text-center text-gray-500 text-sm py-8">{t('empty.leads')}</p>
          ) : (
            byStatus[mobileStatus].map(lead => (
              <div key={lead.id} className="space-y-1">
                <LeadCardContent lead={lead} onOpenDetail={onOpenDetail} onAddActivity={onAddActivity} />
                <div className="flex gap-1">
                  {mobileIdx > 0 && (
                    <button onClick={() => onStatusChange(lead.id, LEAD_STATUSES[mobileIdx - 1])}
                            className="flex-1 text-[11px] text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg py-1.5 flex items-center justify-center gap-1">
                      <ChevronLeft className="w-3 h-3" />{t(`status.${LEAD_STATUSES[mobileIdx - 1]}`)}
                    </button>
                  )}
                  {mobileIdx < LEAD_STATUSES.length - 1 && (
                    <button onClick={() => onStatusChange(lead.id, LEAD_STATUSES[mobileIdx + 1])}
                            className="flex-1 text-[11px] text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg py-1.5 flex items-center justify-center gap-1">
                      {t(`status.${LEAD_STATUSES[mobileIdx + 1]}`)}<ChevronRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  )
}

export default LeadsKanban
